import { Button, Portal } from '@mui/material';
import type { Filter, HotelCalendarPayload } from '@niarab2c/frontend-commons/src/types/hotels';
import { NiaraIcon } from '@niaratech/niara-icons';
import { formatDate } from '@niaratech/niara-js-commons';
import { diffInDays } from '@niaratech/niara-js-commons/dist/dateUtils';
import { Modal2, Tooltip } from '@niaratech/niara-react-components';
import { useEffectOnChange, useIsMobile } from '@niaratech/niara-react-components/src';
import { Form, FormInput, InputToggle, type FormProps } from '@niaratech/niara-react-form';
import type {} from '@niaratech/niara-react-form/src/themes/themeAugmentation';
import parseISO from 'date-fns/parseISO';
import _isEqual from 'lodash/isEqual';
import _map from 'lodash/map';
import _pick from 'lodash/pick';
import type { Client, HotelSearchRule, Person } from 'niara-spear-crudmodel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdCircle } from 'react-icons/md';
import { useHistory } from 'react-router';
import { ContentSearchOption, CounterPeople, useHotelSearchContext } from '../..';
import { IcLocalization, IcPerson } from '../../Icons';
import { Alert, ShoppingCartDropdown, ShoppingCartDropdownProps } from '../../components';
import PersonSearch from '../../components/personSearch/PersonSearch';
import { InputAutoComplete, PromoCode } from '../../microComponents';
import type { DatePickerProps as DatePickerPropsV2 } from '../../microComponents/DatePickerV2/DatePicker';
import DatePickerV2 from '../../microComponents/DatePickerV2/lazy';
import DatePickerV3, { type DatePickerV3Props } from '../../microComponents/DatePickerV3';
import DebugButton from '../../microComponents/DebugButton';
import { GroupingOptions } from '../../microComponents/InputAutoComplete/InputAutoComplete';
import useDefaultPortalContext from '../../portal/useDefaultPortalContext';
import { BREAKPOINT_SM } from '../../theme/ota';
import { formatPoints } from '../../util/common';
import AdvancedFilters from '../Filters/AdvancedFilters';
import ImportPerson from '../ImportPerson/ImportPerson';
import CriteriaThemeProvider from './CriteriaThemeProvider';
import OccupancyInput from './OccupancyInput';
import * as S_NIARA from './styles';
import * as S_DUDA from './stylesDuda';
import useDetailsToCalendar from './useDetailsToCalendar';
const groupingDestinations: GroupingOptions[] = [{
  label: 'Cidades',
  type: 'city',
  icon: <NiaraIcon icon="sysGlobeOutlined" size={26} />
}, {
  label: 'Hotéis',
  type: 'hotel',
  icon: <NiaraIcon icon="sysHotelOutlined" size={26} />
}, {
  label: 'País',
  type: 'country',
  icon: <NiaraIcon icon="htlPinOutlined" size={26} />
}, {
  label: 'Bairros',
  type: 'neighborhood',
  icon: <NiaraIcon icon="sysNeighborhood" size={26} />
}, {
  label: 'Estados',
  type: 'state',
  icon: <NiaraIcon icon="htlPinOutlined" size={26} />
}, {
  label: 'Várias cidades',
  type: 'multi_city_vicinity',
  icon: <NiaraIcon icon="sysMultiCity" size={26} />
}, {
  label: 'Pontos de interesse',
  type: 'point_of_interest',
  icon: <NiaraIcon icon="sysFavorite" size={26} />
}, {
  label: 'Aeroportos',
  type: 'airport',
  icon: <NiaraIcon icon="sysPlane" size={26} />
}, {
  label: 'Regiões de alto nível',
  type: 'high_level_region',
  icon: <NiaraIcon icon="htlPinOutlined" size={26} />
}, {
  label: 'Províncias',
  type: 'province_state',
  icon: <NiaraIcon icon="htlPinOutlined" size={26} />
}, {
  label: 'Continentes',
  type: 'continent',
  icon: <NiaraIcon icon="htlPinOutlined" size={26} />
}, {
  label: 'Estações de trem',
  type: 'train_station',
  icon: <NiaraIcon icon="sysTrain" size={26} />
}, {
  label: 'Estações de metrô',
  type: 'metro_station',
  icon: <NiaraIcon icon="sysTrain" size={26} />
}];
type Destination = {
  label: string | Array<string>;
  value: DestinationValue;
  id: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  name?: string;
  type?: string;
  regionIds?: string[];
};
type ClientOption = Pick<Client, 'id' | 'brand_name' | 'promoCodeEnabled'>;
export const contractStatus = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  BLOCKED: "Não liberado para uso"
};
export type LoyaltyOption = {
  TSER_idContrato?: number;
  TSER_numeroContrato?: string;
  balance?: number;
  unitText?: string;
  contractStatus?: keyof typeof contractStatus;
  allowDecimals?: boolean;
  programAlias?: string;
  programType?: string;
  showTierOnNavbar?: boolean;
  tier?: string;
  tierExpirationDateTime?: string;
  tierUpdateDateTime?: string;
};
type DestinationValue = {
  propertyId?: string;
  contentType?: 'property' | 'region';
  cityIds?: Array<string>;
  hotelIds?: Array<string>;
};
type DestinationSearch = (q: string) => Promise<Array<Destination>>;
type ClientSearch = (q: string) => Array<ClientOption>;
type PersonSearch = (q: string) => Promise<Array<Person>>;
export type RoomInCriteriaForm = {
  adults: number;
  children?: number;
  childrenAges?: number[];
};
export type CriteriaForm = {
  startDate: string;
  endDate: string;
  rooms: RoomInCriteriaForm[];
  destinations?: DestinationValue;
  destinationName?: string | Array<string>;
  bestOnly?: boolean;
  promoCode?: string;
  credentialIds?: string[];
  availRatesOnly?: boolean;
  distributionIds?: string[];
  roomRateIds?: string[];
  enablePromoCode?: boolean;
  clientId?: string;
  clientName?: string;
  personId?: string;
  personName?: string;
  personBalance?: number;
  personTSER_idContrato?: number;
  expectedRoomsCount?: number;
  awards?: number[];
};
export type RestrictionsDetailCalendar = {
  childrenAllowed?: boolean;
  closedOnArrival?: boolean;
  closedOnDeparture?: boolean;
  maxAdultCount?: number;
  maxChildAge?: number;
  maxChildCount?: number;
  maxNightCount?: number;
  minAdultCount?: number;
  minChildAge?: number;
  minChildCount?: number;
  minNightCount?: number;
  minTimeBetweenBookingAndCheckin?: number;
  requiredNightCount?: boolean;
};
export type DetailCalendarDatePickerItems = {
  available?: boolean;
  currency?: string;
  date?: string;
  ratePlan?: string;
  restrictions?: RestrictionsDetailCalendar;
  units?: number;
  value?: number;
};
export interface HotelCriteriaProps {
  personSearchEnabled?: boolean;
  enablePromoCode?: boolean;
  enableClientList?: boolean;
  destinationSearch: DestinationSearch;
  clientSearch?: ClientSearch;
  minStartDate?: string | undefined;
  maxStartDate?: string | undefined;
  onHotelSearch?: (c: CriteriaForm) => any;
  autoFocus?: boolean;
  modifyDates?: boolean;
  headerSize?: string;
  hotelSearchRule?: HotelSearchRule;
  isSearchPage?: boolean;
  detailCalendarStatusToDatePicker?: (c: HotelCalendarPayload['criteria']) => Promise<DetailCalendarDatePickerItems[]>;
  personSearch?: PersonSearch;
  importPerson?: (p: Partial<Person>) => Promise<Person>;
  shoppingCartDropdownProps?: ShoppingCartDropdownProps;
  setValue: React.Dispatch<React.SetStateAction<FormValueType>>;
  value: FormValueType | undefined | null;
  loyaltyOptions?: LoyaltyOption[];
  isLoadingLoyaltyOptions?: boolean;
  calendarMode?: DatePickerPropsV2['initialToggle'];
  forceHideCart?: boolean;
  /**
   * Informa se longStay está ativo no Ota Builder
   */
  isLongStay?: boolean;
  enableLongStayDudaStyle?: boolean;
  enableDateAndMonthSelector?: DatePickerPropsV2['enableDateAndMonthSelector'];
  /**
   * O valor respeitado será o maior entre minNightCount e hotelSearchRule.minNightCount
   ** Se minNightCount = 5 e hotelSearchRule.minNightCount = 10
   ** Será respeitado o hotelSearchRule.minNightCount.
   ** Se minNightCount = 30 e hotelSearchRule.minNightCount = 10
   ** Será respeitado o minNightCount.
   */
  minNightCount?: number;
  /**
   * O valor respeitado será o menor entre maxNightCount e hotelSearchRule.maxNightCount
   ** Se maxNightCount = 5 e hotelSearchRule.maxNightCount = 10
   ** Será respeitado o maxNightCount.
   ** Se maxNightCount = 30 e hotelSearchRule.maxNightCount = 10
   ** Será respeitado o hotelSearchRule.maxNightCount.
   */
  maxNightCount?: number;
  forceDisabledInputDestination?: boolean;
  HTMLFormProps?: FormProps<any>['HTMLFormProps'];
  showAdvancedFilters?: boolean;
}

/**
 * Tipo do formValue/setFormValue
 * TODO: evoluir para usar FormInput em todos os campos e
 * validação com @niaratech/niara-react-form
 */
export type FormValueType = {
  destination: Destination;
  person: Partial<Person>;
  client: ClientOption;
  promoCode: string;
  rooms: RoomInCriteriaForm[];
  startDate: string;
  endDate: string;
  loyaltyOption?: LoyaltyOption;
  expectedRoomsCount?: number;
  awards: number[];
};
const EMPTY_OBJECT = {} as const;

// Verifica se os dois criterias são iguais em alguns atributos para definir se mostra o tooltip de alteração dos dados
const isCriteriaEqual = (criteria1, criteria2) => {
  if (!criteria1 || !criteria2) {
    return false;
  }

  // Verifica cada propriedade individualmente
  return _isEqual(_pick(criteria1?.destination?.value, ['contentType', 'propertyId', 'hotelIds']), _pick(criteria2?.destination?.value, ['contentType', 'propertyId', 'hotelIds'])) && _isEqual(criteria1?.person, criteria2?.person) && _isEqual(criteria1?.client, criteria2?.client) && _isEqual(_map(criteria1?.rooms || [], room => _pick(room, ['adults', 'children', 'childrenAges'])), _map(criteria2?.rooms || [], room => _pick(room, ['adults', 'children', 'childrenAges']))) && _isEqual(criteria1?.startDate, criteria2?.startDate) && _isEqual(criteria1?.endDate, criteria2?.endDate) && _isEqual(criteria1?.promoCode, criteria2?.promoCode);
};
const HotelCriteria: React.FC<HotelCriteriaProps> = ({
  enablePromoCode: propsEnablePromoCode,
  destinationSearch,
  minStartDate,
  maxStartDate,
  onHotelSearch,
  autoFocus,
  modifyDates,
  headerSize,
  hotelSearchRule,
  isSearchPage,
  detailCalendarStatusToDatePicker,
  enableClientList = false,
  clientSearch,
  personSearchEnabled,
  personSearch,
  importPerson,
  shoppingCartDropdownProps,
  value: propsValue,
  setValue: setFormValue,
  loyaltyOptions,
  isLoadingLoyaltyOptions,
  isLongStay,
  enableLongStayDudaStyle,
  calendarMode,
  forceHideCart,
  enableDateAndMonthSelector: enableDateAndMonthSelectorProps,
  maxNightCount: propsMaxNightCount,
  minNightCount: propsMinNightCount,
  forceDisabledInputDestination,
  HTMLFormProps,
  showAdvancedFilters
}) => {
  const S = enableLongStayDudaStyle ? S_DUDA : S_NIARA;
  const enableDateAndMonthSelector = enableDateAndMonthSelectorProps !== undefined ? enableDateAndMonthSelectorProps : isLongStay;
  const formValue = propsValue ?? EMPTY_OBJECT as FormValueType;
  const initialFormValue = useRef(formValue);
  const history = useHistory();
  const {
    checkoutOrAddonsPath,
    isForGroups
  } = useHotelSearchContext();
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<Pick<Filter, 'award'>>({
    award: formValue?.awards?.map(award => Number(award))
  });
  const onSearch = useCallback((criteria: CriteriaForm) => {
    onHotelSearch(criteria);
  }, [onHotelSearch]);
  const {
    minAdultCount,
    maxAdultCount,
    childrenAllowed = true,
    minChildCount,
    minChildAge,
    maxChildAge,
    minRoomCount,
    maxRoomCount,
    notDisplayLocationInSearch
  } = hotelSearchRule || {};
  const maxChildCount = hotelSearchRule?.maxChildCount ?? 3;
  const [openImportPerson, setOpenImportPerson] = useState(false);
  const {
    destination,
    client,
    rooms: valueRooms,
    startDate,
    endDate
  } = formValue;

  /**
   * Renderiza uma opção do autocomplete de pessoas
   * @param person
   * @param q
   * @returns
   */
  const PersonRenderOption = (person: Person, q?: string) => {
    let showResult = '';
    let actionQuery = q;
    const cleanedQ = q?.replace(/\./g, '')?.replace(/-/g, ''); // Remove todos os pontos e hífens do que foi digitado no campo
    const completeName = [person?.firstName, person?.lastName].filter(Boolean).join(' ');
    const loyaltyTiers = person?.loyaltyTiers ? Object.values(person?.loyaltyTiers)?.filter(Boolean)?.map((tier: string | Record<string, any>) => typeof tier === 'string' ? tier : tier?.name)?.filter(Boolean) : [];

    //#179513
    //Valida se existem letras na string digitada no campo.
    //Caso tenha, a busca segue com o que foi digitado pois nao se trata de um CPF.
    //Caso nao tenha letras e tenha apenas numeros, assumi que é o usuario digitou um CPF ou parte dele e ignoro pontos e hífens digitados pelo usuario.
    if (/^\d+$/.test(cleanedQ)) {
      actionQuery = cleanedQ;
    }
    Object.keys(person).forEach(item => {
      const propertyValue = person[item] as string;

      // Remove pontos e hífens apenas se a propriedade atual parecer ser um CPF #179513
      let cleanedPropertyValue = propertyValue;
      if (/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(propertyValue)) {
        cleanedPropertyValue = propertyValue.replace(/\./g, '').replace(/-/g, '');
      }
      if (cleanedPropertyValue?.toUpperCase?.()?.includes(actionQuery?.toUpperCase?.()) && !['email', 'firstName', 'lastName', 'clientId', 'id']?.includes(item) && actionQuery != '') {
        let nameKey = '';
        switch (item) {
          case 'cpf':
            nameKey = "CPF";
            break;
          case 'phone':
            nameKey = "Telefone";
            break;
          case 'passport':
            nameKey = "Passaporte";
        }
        showResult = `${nameKey != '' ? `${nameKey}: ${propertyValue}` : `${propertyValue}`}`;
      }
    });
    return <S.RenderOption>
        <S.OptionTitleContainer>
          <S.Text>{completeName}</S.Text>
          {loyaltyTiers.length > 0 && loyaltyTiers.map((tier, idx) => <S.LoyaltyLevelBadge key={idx}>
                <S.Text>{tier}</S.Text>
              </S.LoyaltyLevelBadge>)}
        </S.OptionTitleContainer>
        <S.TextQuery>{person?.email}</S.TextQuery>
        <S.TextQuery>{showResult}</S.TextQuery>
      </S.RenderOption>;
  };
  const loyaltyOptionRenderOption = (option: LoyaltyOption) => {
    return <S.RenderOptionItem>
        <S.RenderOption>
          <S.Text>{option?.TSER_numeroContrato}</S.Text>
          <S.TextQuery>
            {formatPoints(option?.balance)} {option?.unitText}
          </S.TextQuery>
        </S.RenderOption>
        <S.RenderOption className="align-right">
          <MdCircle color={option?.contractStatus == 'ACTIVE' ? '#53AC2A' : '#FF0000'} />
          <S.TextQuery>{contractStatus[option?.contractStatus]}</S.TextQuery>
        </S.RenderOption>
      </S.RenderOptionItem>;
  };
  const NoResultsMessage = () => {
    return <S.RenderNoOption>
        <S.Text>Nenhum resultado encontrado</S.Text>
        <Button variant="text" color="info" size="small" onClick={() => setOpenImportPerson(true)}>
          Importar uma pessoa
        </Button>
      </S.RenderNoOption>;
  };
  const setPerson = useCallback((person: Partial<Person>) => {
    setFormValue?.(prev => ({
      ...prev,
      person
    }));
  }, [setFormValue]);
  const setExpectedRoomsCount = useCallback((roomsCount: number) => {
    setFormValue(old => ({
      ...old,
      expectedRoomsCount: roomsCount
    }));
  }, [setFormValue]);
  const [size, setSize] = useState<number>();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [breakpoint, setBreakpoint] = useState<boolean>();
  const missingChildrenAges = useMemo(() => {
    if (formValue?.rooms?.length > 0) {
      for (const room of formValue?.rooms) {
        for (let i = 0; i < room?.children ?? 0; i++) {
          if (room?.childrenAges[i] == null) return true;
        }
      }
    }
    return false;
  }, [formValue]);
  const personDisabled = !personSearchEnabled || client == null ? true : false;
  const [queryPerson, setQueryPerson] = useState('');
  useEffect(() => {
    // ao trocar de cliente, zerar o campo de pessoa
    setFormValue?.(formValue => {
      const person = formValue?.person;
      if (!person) return formValue;
      if (!client) return {
        ...formValue,
        person: null,
        loyaltyOption: null
      };
      if (client.id != person.clientId) return {
        ...formValue,
        person: null,
        loyaltyOption: null
      };
      return formValue;
    });
  }, [client, setFormValue]);
  useEffect(() => {
    if (personDisabled) {
      setFormValue?.(formValue => {
        if (formValue?.person) return {
          ...formValue,
          person: null,
          loyaltyOption: null
        };
        return formValue;
      });
    }
  }, [personDisabled, setFormValue]);
  useEffect(() => {
    //popula os demais campos do contrato a partir do idContrato. É o caso em que já tinha salvo em outra busca
    const contractId = formValue?.loyaltyOption?.TSER_idContrato;
    const contractNumber = formValue?.loyaltyOption?.TSER_numeroContrato;
    if (contractId && !contractNumber && loyaltyOptions?.length > 0) {
      const loyaltyOption = loyaltyOptions?.find(o => o?.TSER_idContrato == contractId);
      setFormValue?.(formValue => {
        return {
          ...formValue,
          loyaltyOption
        };
      });
    }
  }, [formValue?.loyaltyOption?.TSER_idContrato, formValue?.loyaltyOption?.TSER_numeroContrato, loyaltyOptions, setFormValue]);
  useEffect(() => {
    // limpa o contrato quando teve alteração de pessoa
    const contractId = formValue?.loyaltyOption?.TSER_idContrato;
    if (contractId && !isLoadingLoyaltyOptions && loyaltyOptions?.length > 0) {
      const loyaltyOption = loyaltyOptions?.find(o => o?.TSER_idContrato == contractId);
      if (!loyaltyOption) setFormValue?.(formValue => ({
        ...formValue,
        loyaltyOption: null
      }));
    }
  }, [formValue?.loyaltyOption?.TSER_idContrato, isLoadingLoyaltyOptions, loyaltyOptions, setFormValue]);
  const enablePromoCode = propsEnablePromoCode != undefined ? propsEnablePromoCode : client?.promoCodeEnabled;

  // state que controla o estado do toggle
  const [enableTogglePerson, setEnableTogglePerson] = useState(() => {
    if (formValue?.person?.id) return true;
    return false;
  });
  const onTogglePerson = () => {
    setEnableTogglePerson(prev => {
      if (prev === true) {
        // ao desmarcar busca por hóspede cadastrado, limpa a pessoa que estava selecionada
        setFormValue?.(formValue => ({
          ...formValue,
          person: null,
          loyaltyOption: null
        }));
      }
      return !prev;
    });
  };
  const minDate = React.useMemo(() => {
    if (minStartDate) return parseISO(minStartDate);
    return new Date();
  }, [minStartDate]);
  const maxDate = React.useMemo(() => {
    if (maxStartDate) return parseISO(maxStartDate);
    return undefined;
  }, [maxStartDate]);
  useEffectOnChange(() => {
    // mostra tooltip de alerta de mudança de critérios
    if (isSearchPage) {
      if (!isCriteriaEqual(formValue, initialFormValue.current) && formValue?.endDate) setShowTooltip(true);else setShowTooltip(false);
    }
  }, [formValue, isSearchPage]);
  const nights = useMemo(() => diffInDays(endDate, startDate) || 0, [endDate, startDate]);
  const minNightCount = useMemo(() => {
    return Math.max(propsMinNightCount ?? 1, hotelSearchRule?.minNightCount ?? 1);
  }, [hotelSearchRule?.minNightCount, propsMinNightCount]);
  const maxNightCount = useMemo(() => {
    return Math.min(propsMaxNightCount ?? Number.MAX_SAFE_INTEGER, hotelSearchRule?.maxNightCount ?? Number.MAX_SAFE_INTEGER);
  }, [hotelSearchRule?.maxNightCount, propsMaxNightCount]);
  const datePickerErrorMessages: string[] | undefined = useMemo(() => {
    const errors: string[] = [];
    if (minStartDate && startDate && startDate < minStartDate) {
      errors.push(`Data de início mínima é ${formatDate(minStartDate)}`);
    }
    if (nights < minNightCount) {
      errors.push(`Número mínimo de noites é ${minNightCount}`);
    }
    if (nights > maxNightCount) {
      errors.push(`Número máximo de noites é ${maxNightCount}`);
    }
    if (errors.length > 0) return errors;
    return undefined;
  }, [maxNightCount, minNightCount, minStartDate, nights, startDate]);

  /**
   * flag para mostrar os erros após tentativa de submit
   */
  const [showCustomErrors, setShowCustomErrors] = useState(false);
  const onSubmit = useCallback((values: FormValueType) => {
    const person = values.person;
    setShowCustomErrors(true);
    if (!missingChildrenAges && !datePickerErrorMessages && destination?.value != null) {
      setShowTooltip(false);
      onSearch({
        endDate: values?.endDate,
        startDate: values?.startDate,
        rooms: values?.rooms,
        destinations: values?.destination?.value,
        destinationName: values?.destination?.label,
        promoCode: values?.promoCode,
        clientName: values?.client?.brand_name,
        clientId: values?.client?.id,
        enablePromoCode,
        personId: person?.id,
        personName: [person?.firstName, person?.lastName].filter(Boolean).join(' '),
        personTSER_idContrato: values?.loyaltyOption?.TSER_idContrato,
        expectedRoomsCount: values?.expectedRoomsCount,
        awards: filters?.award
      });
    }
  }, [datePickerErrorMessages, destination?.value, enablePromoCode, filters?.award, missingChildrenAges, onSearch]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSize(window.innerWidth);
    }
    const onResize = () => {
      setSize(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    setBreakpoint(size < +BREAKPOINT_SM.replace('px', ''));
  }, [size]);
  const guestsQtd = valueRooms?.reduce((acc, item) => acc + item.adults + (item?.children ?? 0), 0);
  const currentHotelIdRef = useRef(formValue?.destination?.value?.hotelIds?.[0]);
  currentHotelIdRef.current = formValue?.destination?.value?.hotelIds?.[0];
  const [detailsToCalendar, onShownDateChange, loadingCalendar] = useDetailsToCalendar({
    clientId: formValue?.client?.id,
    maxDate,
    minDate,
    adults: formValue?.rooms?.[0]?.adults,
    children: formValue?.rooms?.[0]?.children,
    childrenAges: formValue?.rooms?.[0]?.childrenAges,
    propertyId: formValue?.destination?.value?.propertyId,
    contentType: formValue?.destination?.value?.contentType,
    promoCode: formValue?.promoCode,
    hotelId: formValue?.destination?.value?.hotelIds?.length == 1 ? formValue?.destination?.value?.hotelIds[0] : undefined // não deveria ser obrigatório, mas calendar está exigindo
  }, detailCalendarStatusToDatePicker);
  const onCheckout = useCallback(() => {
    history.push(checkoutOrAddonsPath);
  }, [history, checkoutOrAddonsPath]);
  const portalContainer = useDefaultPortalContext()?.container;
  const refComponentForCalendar = useRef(null);
  const getIsButtonEnabled = () => {
    const hasPerson = !!formValue?.person;
    const hasContract = !!formValue?.loyaltyOption?.TSER_idContrato;
    const hasLoyaltyOptions = loyaltyOptions?.length > 0;
    if (nights === 0 || isLoadingLoyaltyOptions) return true;
    if (hasPerson && hasLoyaltyOptions) return !hasContract;
    return false;
  };
  const showAdvancedCriteria = personSearchEnabled && personSearch || showAdvancedFilters;
  return <CriteriaThemeProvider>
      <S.Container id="hotel-criteria" ref={refComponentForCalendar}>
        <S.ContainerHotelCriteria>
          <Form HTMLFormProps={HTMLFormProps} value={formValue} setValue={setFormValue} onSubmit={onSubmit}>
            <S.ContainerGrid marginBottom={isSearchPage ? '20px' : '0'}>
              <S.Grid className="destination-field">
                <S.TitleInputs>{isLongStay ? "Destino ou Condomínio" : "Destino ou Hotel"}</S.TitleInputs>
                <DebugButton value={formValue} />
                <FormInput component={InputAutoComplete} fieldPath="destination" className="destination-autocomplete" placeholder={isLongStay ? 'Escolha' : 'Para onde você vai?'} errors={showCustomErrors && destination?.value == null ? ["Por favor, escolha o destino"] : undefined} data-testid="hotelcriteria-destination" Icon={IcLocalization} singleOptionAutoSelect={true} debounce={600} listSize={50} required={true} suggestionsSource={destinationSearch} renderOption={(option: Destination, query) => {
                return <ContentSearchOption {...option} query={query} notDisplayLocationInSearch={notDisplayLocationInSearch} />;
              }} autoFocus={autoFocus} suggestionToString={(destination: Destination) => typeof destination?.label == 'string' ? destination?.label : 'join' in destination?.label ? destination?.label.join(', ') : '?'} modalTitle="Selecione o destino" groupingOptions={hotelSearchRule?.enableHotelGroupingByCity ? undefined : groupingDestinations} groupingFilter="type" endIcon={enableLongStayDudaStyle ? <NiaraIcon icon="sysArrowDown" /> : undefined} rotateEndIconWhenOpen={enableLongStayDudaStyle} disabled={forceDisabledInputDestination} />
              </S.Grid>
              <S.Grid className="date-field">
                {isLongStay ? <S.TitleInputs>Data de Entrada</S.TitleInputs> : <S.TitleInputs>
                    Check-in / Check-out {nights > 1 ? <span>{nights} Noites</span> : <span>{nights} Noite</span>}
                  </S.TitleInputs>}
                <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                  {enableLongStayDudaStyle && isMobile && <NiaraIcon icon={'sysCalendar'} size={22} />}
                  {isLongStay ? <FormInput minDiffDays={minNightCount} enforceMinDiffDaysOnSelection={true} component={DatePickerV2} fieldMapping={{
                  startDate: 'startDate',
                  endDate: 'endDate'
                }} minDate={minDate} maxDate={maxDate} initOpened={modifyDates} headerSize={headerSize} detailsToCalendar={detailsToCalendar} errors={showCustomErrors && datePickerErrorMessages ? datePickerErrorMessages : undefined} onShownDateChange={onShownDateChange} onShown={() => {
                  if (onShownDateChange) {
                    onShownDateChange(startDate);
                  }
                }} loading={loadingCalendar} personSearchEnabled={personSearchEnabled} data-testid="hotelcriteria-checkin-checkout" referenceComponent={refComponentForCalendar} enableDateAndMonthSelector={enableDateAndMonthSelector} initialToggle={calendarMode} /> : <FormInput<DatePickerV3Props, DatePickerV3Props['value']> component={DatePickerV3} fieldMapping={{
                  startDate: 'startDate',
                  endDate: 'endDate'
                }} minNightCount={minNightCount} min={minStartDate} max={maxStartDate} initOpened={modifyDates} detailsToCalendar={detailsToCalendar} errors={showCustomErrors && datePickerErrorMessages ? datePickerErrorMessages : undefined} onShownMonthChange={onShownDateChange} onShown={() => {
                  if (onShownDateChange) {
                    onShownDateChange(startDate);
                    setShowTooltip(false);
                  }
                }} loading={loadingCalendar} data-testid="hotelcriteria-checkin-checkout" referenceComponent={refComponentForCalendar}
                // enableDateAndMonthSelector={enableDateAndMonthSelector}
                // initialToggle={calendarMode}
                />}
                </div>
              </S.Grid>

              {!isLongStay && <S.Grid className="occupancy-field">
                  {breakpoint ? <FormInput component={OccupancyInput} fieldPath="rooms" childrenAllowed={childrenAllowed} maxAdultCount={maxAdultCount} maxChildAge={maxChildAge} maxChildCount={maxChildCount} minAdultCount={minAdultCount} minChildAge={minChildAge} minChildCount={minChildCount} minRoomCount={minRoomCount} maxRoomCount={maxRoomCount} occupancyMode={isForGroups} dataTestId="hotelcriteria-occupancy" /> : <>
                      <S.TitleInputs>{isForGroups ? "Quartos / Ocupações" : "Hóspedes"}</S.TitleInputs>

                      <CounterPeople qtdeRooms={formValue?.expectedRoomsCount ?? 1} guests={guestsQtd ?? 0} errors={showCustomErrors && missingChildrenAges ? ["Falta selecionar a idade das crianças"] : null} data-testid="hotelcriteria-occupancy-button" isForGroups={isForGroups} countOccupancy={valueRooms?.length ?? 0}>
                        <FormInput component={OccupancyInput} fieldPath="rooms" childrenAllowed={childrenAllowed} maxAdultCount={maxAdultCount} maxChildAge={maxChildAge} maxChildCount={maxChildCount} minAdultCount={minAdultCount} minChildAge={minChildAge} minChildCount={minChildCount} minRoomCount={minRoomCount} maxRoomCount={maxRoomCount} occupancyMode={isForGroups} expectedRoomsCount={formValue?.expectedRoomsCount ?? 1} setExpectedRoomsCount={setExpectedRoomsCount} dataTestId="hotelcriteria-occupancy-paper" />
                      </CounterPeople>
                    </>}
                </S.Grid>}
              {enableClientList && <S.Grid className="client-field">
                  <S.HeaderClient>
                    <S.TitleInputs>Cliente</S.TitleInputs>
                  </S.HeaderClient>
                  <FormInput component={InputAutoComplete} fieldPath="client" placeholder="Selecionar cliente" debounce={600} suggestionsSource={clientSearch} modalTitle="Selecione o cliente" required errors={showCustomErrors && client === undefined ? ["Por favor, escolha o cliente"] : undefined} suggestionToString={(client: ClientOption) => client?.brand_name} data-testid="hotelcriteria-client" />
                </S.Grid>}
              {enablePromoCode && <S.Grid className="promocode-field">
                  <S.TitleInputs>Tenho um código</S.TitleInputs>
                  <FormInput component={PromoCode} fieldPath="promoCode" dataTestId="hotelcriteria-promocode" name="promoCode" placeholder="Meu código..." />
                </S.Grid>}
              <S.ButtonSearchCartStyleDiv>
                <S.ButtonStyle>
                  {isMobile ? <Button /* removi tooltip no mobile, pois conflita com o seletor de datas */ data-testid="hotelcriteria-action-search" color="primary" disabled={getIsButtonEnabled()} type="submit" fullWidth size={isMobile ? 'extraLarge' : 'input'} variant="contained" sx={isLoadingLoyaltyOptions ? {
                  paddingLeft: '5px',
                  paddingRight: '5px'
                } : undefined} // alterado padding conforme tíquete #215471
                >
                      {isLoadingLoyaltyOptions ? <NiaraIcon icon={'sysLoading'} style={{
                    marginRight: '2px'
                  }} /> : null}
                      Buscar
                    </Button> : <Tooltip title={<div style={{
                  fontSize: 14,
                  padding: 10,
                  fontWeight: 300
                }}>
                          Alterou seus dados de estadia. Busque novamente!!
                        </div>} open={showTooltip} placement="bottom" arrow={true}
                // fontSize={14}
                // padding={10}
                // overflowY="unset"
                onClose={() => setShowTooltip(false)} leaveTouchDelay={1500}
                // fontWeigth={300}
                PopperProps={{
                  disablePortal: false,
                  modifiers: [{
                    name: 'flip',
                    enabled: true,
                    options: {
                      fallbackPlacements: ['top', 'bottom-start', 'bottom-end']
                    }
                  }]
                }}>
                      <Button data-testid="hotelcriteria-action-search" color="primary" disabled={getIsButtonEnabled()} type="submit" fullWidth size={isMobile ? 'extraLarge' : 'input'} variant="contained" sx={isLoadingLoyaltyOptions && {
                    paddingLeft: '5px',
                    paddingRight: '5px'
                  }} // alterado padding conforme tíquete #215471
                  >
                        {isLoadingLoyaltyOptions ? <NiaraIcon icon={'sysLoading'} style={{
                      marginRight: '2px'
                    }} /> : null}
                        Buscar
                      </Button>
                    </Tooltip>}
                </S.ButtonStyle>

                {!isLongStay && !forceHideCart && <S.ShoppingCartDropdown>
                    <ShoppingCartDropdown handleClickCheckout={onCheckout} {...shoppingCartDropdownProps} hasSearchTooltipOpened={showTooltip} expectedRoomsCount={formValue?.expectedRoomsCount} isForGroups={isForGroups} />
                  </S.ShoppingCartDropdown>}
              </S.ButtonSearchCartStyleDiv>
            </S.ContainerGrid>

            {!isForGroups && showAdvancedCriteria && <>
                <S.ContainerGrid>
                  {personSearchEnabled && personSearch && <>
                      <S.Grid className="toggle-person-field">
                        <S.InputToggle data-testid="hotelcriteria-toggle-search-people">
                          <InputToggle value={enableTogglePerson} setValue={onTogglePerson} disabled={personDisabled} id="toggle-search-people" />
                          <label htmlFor="toggle-search-people">Buscar hóspede cadastrado</label>
                        </S.InputToggle>
                      </S.Grid>

                      {enableTogglePerson && <S.Grid className="person-field">
                          <FormInput key={client?.id} component={InputAutoComplete} fieldPath="person" placeholder="Digite o email ou CPF do hóspede." modalTitle="Selecione a pessoa" Icon={IcPerson} required singleOptionAutoSelect={false} onTextChanged={setQueryPerson} debounce={600} suggestionsSource={personSearch} suggestionToString={(person: Person) => person ? [person?.firstName, person?.lastName]?.filter(Boolean)?.join(' ') : null} noResultsMessage={importPerson ? <NoResultsMessage /> : undefined} renderOption={PersonRenderOption} />
                        </S.Grid>}

                      {enableTogglePerson && (loyaltyOptions?.length > 0 || formValue?.loyaltyOption?.TSER_idContrato) && <S.Grid className="loyalty-option-field">
                            <FormInput key={client?.id} component={InputAutoComplete} fieldPath="loyaltyOption" placeholder={isLoadingLoyaltyOptions ? "Carregando os contratos..." : "Selecione o contrato"} singleOptionAutoSelect={true} suggestionsSource={(q: string) => loyaltyOptions?.filter(o => !q || o?.TSER_numeroContrato?.toLowerCase()?.indexOf(q?.toLowerCase()) > 0)} modalTitle="Selecione o contrato" suggestionToString={(loyaltyOption: LoyaltyOption) => loyaltyOption?.TSER_numeroContrato} renderOption={loyaltyOptionRenderOption} disabled={!formValue?.person?.id || isLoadingLoyaltyOptions} />
                          </S.Grid>}
                    </>}
                  {showAdvancedFilters && <AdvancedFilters filters={filters} setFilters={setFilters} />}
                </S.ContainerGrid>
              </>}
          </Form>
        </S.ContainerHotelCriteria>
        {isLongStay && nights >= 365 &&
      // ver #220496
      <Alert severity="warning" style={{
        marginTop: '20px'
      }}>
            Para reservas a partir de 12 meses favor entrar em contato com nossa Central de Reservas pelo telefone (11)
            3059-3277 ou e-mail contato@econfor.com
          </Alert>}
      </S.Container>
      {importPerson && <Portal container={portalContainer}>
          <Modal2 show={openImportPerson} setShow={setOpenImportPerson} mobileVariant="full" render={({
        hide
      }) => <ImportPerson hide={hide} cpf={queryPerson} setPerson={setPerson} importPerson={importPerson} />} zIndex={2247483657} />
        </Portal>}
    </CriteriaThemeProvider>;
};
export default HotelCriteria;